import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Divider,
  Grid,
} from '@mui/material';
import {
  getPricingCatalogAdmin,
  resetPricingCatalog,
  savePricingPlan,
  deletePricingPlan,
  savePricingPromo,
  deletePricingPromo,
  listPricingPlans,
} from '../../api/rlApi';

const emptyPlan = {
  name: '',
  slug: '',
  description: '',
  monthly_price: 0,
  annual_price: 0,
  currency: 'USD',
  trial_days: 0,
  seats_included: 1,
  usage_notes: '',
  features: [],
  tags: [],
  badge: '',
  recommended: false,
  status: 'draft',
  order: 0,
  cta_label: 'Book a demo',
  cta_link: '/demo',
  support: '',
  limits: '',
};

const emptyPromo = {
  name: '',
  type: 'percent',
  value: 10,
  applies_to_all: true,
  applies_to: [],
  coupon_code: '',
  start_at: '',
  end_at: '',
  badge: '',
  priority: 0,
  status: 'active',
  stackable: false,
  max_discount_pct: 90,
  min_price: 0,
};

function FeatureTextarea({ value, onChange }) {
  return (
    <TextField
      label="Features (one per line)"
      multiline
      minRows={4}
      value={(value || []).join('\n')}
      onChange={(e) => onChange(e.target.value.split('\n').map((f) => f.trim()).filter(Boolean))}
      fullWidth
    />
  );
}

export default function PricingAdmin() {
  const [adminToken, setAdminToken] = useState('');
  const [catalog, setCatalog] = useState({ plans: [], promos: [] });
  const [planForm, setPlanForm] = useState(emptyPlan);
  const [promoForm, setPromoForm] = useState(emptyPromo);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [selectedPromoId, setSelectedPromoId] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [previewPlans, setPreviewPlans] = useState([]);

  const loadCatalog = async () => {
    try {
      setMessage('');
      setError('');
      const res = await getPricingCatalogAdmin(adminToken || undefined);
      setCatalog({ plans: res.plans || [], promos: res.promos || [] });
    } catch (err) {
      setError('Unable to load pricing catalog (check token).');
    }
  };

  const loadPreview = async () => {
    try {
      const res = await listPricingPlans();
      setPreviewPlans(res.plans || []);
    } catch (e) {
      setPreviewPlans([]);
    }
  };

  useEffect(() => {
    loadPreview();
  }, []);

  const handlePlanSelect = (planId) => {
    setSelectedPlanId(planId);
    const plan = catalog.plans.find((p) => p.id === planId);
    if (plan) {
      setPlanForm({
        ...plan,
      });
    }
  };

  const handlePromoSelect = (promoId) => {
    setSelectedPromoId(promoId);
    const promo = catalog.promos.find((p) => p.id === promoId);
    if (promo) {
      setPromoForm({
        ...promo,
      });
    }
  };

  const savePlan = async () => {
    try {
      const res = await savePricingPlan(planForm, adminToken || undefined);
      setMessage(`Saved plan ${res.plan?.name || ''}`);
      setError('');
      await loadCatalog();
      await loadPreview();
      setSelectedPlanId(res.plan?.id || '');
    } catch (err) {
      setError('Failed to save plan (check token or fields).');
    }
  };

  const removePlan = async () => {
    if (!selectedPlanId) return;
    try {
      await deletePricingPlan(selectedPlanId, adminToken || undefined);
      setMessage('Plan deleted');
      setPlanForm(emptyPlan);
      setSelectedPlanId('');
      await loadCatalog();
      await loadPreview();
    } catch (err) {
      setError('Failed to delete plan.');
    }
  };

  const savePromoHandler = async () => {
    try {
      const res = await savePricingPromo(promoForm, adminToken || undefined);
      setMessage(`Saved promo ${res.promo?.name || ''}`);
      setError('');
      await loadCatalog();
      await loadPreview();
      setSelectedPromoId(res.promo?.id || '');
    } catch (err) {
      setError('Failed to save promo.');
    }
  };

  const removePromo = async () => {
    if (!selectedPromoId) return;
    try {
      await deletePricingPromo(selectedPromoId, adminToken || undefined);
      setMessage('Promo deleted');
      setPromoForm(emptyPromo);
      setSelectedPromoId('');
      await loadCatalog();
      await loadPreview();
    } catch (err) {
      setError('Failed to delete promo.');
    }
  };

  const resetCatalogHandler = async () => {
    try {
      await resetPricingCatalog(adminToken || undefined);
      setMessage('Catalog reset to defaults');
      setError('');
      setPlanForm(emptyPlan);
      setPromoForm(emptyPromo);
      setSelectedPlanId('');
      setSelectedPromoId('');
      await loadCatalog();
      await loadPreview();
    } catch (err) {
      setError('Failed to reset catalog.');
    }
  };

  const planPreview = useMemo(() => {
    if (!selectedPlanId) return null;
    return previewPlans.find((p) => p.id === selectedPlanId || p.slug === selectedPlanId);
  }, [previewPlans, selectedPlanId]);

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <TextField
            label="Admin token (env PRICING_ADMIN_TOKEN)"
            value={adminToken}
            onChange={(e) => setAdminToken(e.target.value)}
            size="small"
            sx={{ minWidth: 260 }}
          />
          <Button variant="contained" onClick={loadCatalog}>
            Load catalog
          </Button>
          <Button variant="outlined" onClick={resetCatalogHandler}>
            Reset to defaults
          </Button>
        </Stack>
        {message && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="plan-select-label">Select plan</InputLabel>
                  <Select
                    labelId="plan-select-label"
                    label="Select plan"
                    size="small"
                    value={selectedPlanId}
                    onChange={(e) => handlePlanSelect(e.target.value)}
                  >
                    {catalog.plans.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name} ({p.slug})
                      </MenuItem>
                    ))}
                    <MenuItem value="">New plan</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="outlined" onClick={() => setPlanForm(emptyPlan)}>
                  Clear
                </Button>
              </Stack>

              <TextField
                label="Name"
                fullWidth
                value={planForm.name}
                onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
              />
              <TextField
                label="Slug"
                fullWidth
                value={planForm.slug}
                onChange={(e) => setPlanForm({ ...planForm, slug: e.target.value })}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                minRows={2}
                value={planForm.description}
                onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Monthly price"
                  type="number"
                  value={planForm.monthly_price}
                  onChange={(e) => setPlanForm({ ...planForm, monthly_price: Number(e.target.value) })}
                  fullWidth
                />
                <TextField
                  label="Annual price (optional)"
                  type="number"
                  value={planForm.annual_price ?? ''}
                  onChange={(e) => setPlanForm({ ...planForm, annual_price: Number(e.target.value) })}
                  fullWidth
                />
                <TextField
                  label="Currency"
                  value={planForm.currency}
                  onChange={(e) => setPlanForm({ ...planForm, currency: e.target.value })}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Trial days"
                  type="number"
                  value={planForm.trial_days}
                  onChange={(e) => setPlanForm({ ...planForm, trial_days: Number(e.target.value) })}
                  fullWidth
                />
                <TextField
                  label="Seats included"
                  type="number"
                  value={planForm.seats_included}
                  onChange={(e) =>
                    setPlanForm({ ...planForm, seats_included: Number(e.target.value) })
                  }
                  fullWidth
                />
                <TextField
                  label="Usage notes"
                  value={planForm.usage_notes}
                  onChange={(e) => setPlanForm({ ...planForm, usage_notes: e.target.value })}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Badge"
                  value={planForm.badge}
                  onChange={(e) => setPlanForm({ ...planForm, badge: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="CTA label"
                  value={planForm.cta_label}
                  onChange={(e) => setPlanForm({ ...planForm, cta_label: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="CTA link"
                  value={planForm.cta_link}
                  onChange={(e) => setPlanForm({ ...planForm, cta_link: e.target.value })}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Support"
                  value={planForm.support}
                  onChange={(e) => setPlanForm({ ...planForm, support: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Limits"
                  value={planForm.limits}
                  onChange={(e) => setPlanForm({ ...planForm, limits: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Order"
                  type="number"
                  value={planForm.order}
                  onChange={(e) => setPlanForm({ ...planForm, order: Number(e.target.value) })}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    label="Status"
                    value={planForm.status}
                    onChange={(e) => setPlanForm({ ...planForm, status: e.target.value })}
                    size="small"
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={planForm.recommended}
                      onChange={(e) => setPlanForm({ ...planForm, recommended: e.target.checked })}
                    />
                  }
                  label="Recommended"
                />
              </Stack>

              <FeatureTextarea
                value={planForm.features}
                onChange={(features) => setPlanForm({ ...planForm, features })}
              />

              <Stack direction="row" spacing={2}>
                <Button variant="contained" onClick={savePlan}>
                  Save plan
                </Button>
                <Button variant="outlined" color="error" onClick={removePlan} disabled={!selectedPlanId}>
                  Delete plan
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="promo-select-label">Select promo</InputLabel>
                  <Select
                    labelId="promo-select-label"
                    label="Select promo"
                    size="small"
                    value={selectedPromoId}
                    onChange={(e) => handlePromoSelect(e.target.value)}
                  >
                    {catalog.promos.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                    <MenuItem value="">New promo</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="outlined" onClick={() => setPromoForm(emptyPromo)}>
                  Clear
                </Button>
              </Stack>

              <TextField
                label="Name"
                fullWidth
                value={promoForm.name}
                onChange={(e) => setPromoForm({ ...promoForm, name: e.target.value })}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="promo-type-label">Type</InputLabel>
                  <Select
                    labelId="promo-type-label"
                    label="Type"
                    size="small"
                    value={promoForm.type}
                    onChange={(e) => setPromoForm({ ...promoForm, type: e.target.value })}
                  >
                    <MenuItem value="percent">Percent</MenuItem>
                    <MenuItem value="fixed">Fixed amount</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Value"
                  type="number"
                  value={promoForm.value}
                  onChange={(e) => setPromoForm({ ...promoForm, value: Number(e.target.value) })}
                  fullWidth
                />
                <TextField
                  label="Priority"
                  type="number"
                  value={promoForm.priority}
                  onChange={(e) => setPromoForm({ ...promoForm, priority: Number(e.target.value) })}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Coupon code (optional)"
                  value={promoForm.coupon_code}
                  onChange={(e) => setPromoForm({ ...promoForm, coupon_code: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Badge (optional)"
                  value={promoForm.badge}
                  onChange={(e) => setPromoForm({ ...promoForm, badge: e.target.value })}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Start at (ISO, optional)"
                  value={promoForm.start_at}
                  onChange={(e) => setPromoForm({ ...promoForm, start_at: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="End at (ISO, optional)"
                  value={promoForm.end_at}
                  onChange={(e) => setPromoForm({ ...promoForm, end_at: e.target.value })}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Max discount %"
                  type="number"
                  value={promoForm.max_discount_pct}
                  onChange={(e) =>
                    setPromoForm({ ...promoForm, max_discount_pct: Number(e.target.value) })
                  }
                  fullWidth
                />
                <TextField
                  label="Min price"
                  type="number"
                  value={promoForm.min_price}
                  onChange={(e) => setPromoForm({ ...promoForm, min_price: Number(e.target.value) })}
                  fullWidth
                />
              </Stack>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={promoForm.applies_to_all}
                      onChange={(e) => setPromoForm({ ...promoForm, applies_to_all: e.target.checked })}
                    />
                  }
                  label="Applies to all plans"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={promoForm.stackable}
                      onChange={(e) => setPromoForm({ ...promoForm, stackable: e.target.checked })}
                    />
                  }
                  label="Stackable"
                />
              </FormGroup>

              {!promoForm.applies_to_all && (
                <FormGroup>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Applies to plans
                  </Typography>
                  {catalog.plans.map((plan) => (
                    <FormControlLabel
                      key={plan.id}
                      control={
                        <Checkbox
                          checked={promoForm.applies_to?.includes(plan.id) || promoForm.applies_to?.includes(plan.slug)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const id = plan.id;
                            let next = promoForm.applies_to || [];
                            if (checked && !next.includes(id)) next = [...next, id];
                            if (!checked) next = next.filter((x) => x !== id && x !== plan.slug);
                            setPromoForm({ ...promoForm, applies_to: next });
                          }}
                        />
                      }
                      label={`${plan.name} (${plan.slug})`}
                    />
                  ))}
                </FormGroup>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="promo-status-label">Status</InputLabel>
                  <Select
                    labelId="promo-status-label"
                    label="Status"
                    size="small"
                    value={promoForm.status}
                    onChange={(e) => setPromoForm({ ...promoForm, status: e.target.value })}
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction="row" spacing={2}>
                <Button variant="contained" onClick={savePromoHandler}>
                  Save promo
                </Button>
                <Button variant="outlined" color="error" onClick={removePromo} disabled={!selectedPromoId}>
                  Delete promo
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Preview (published, current promos)
            </Typography>
            {previewPlans.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No published plans yet.
              </Typography>
            )}
            {previewPlans.map((plan) => (
              <Box key={plan.id} sx={{ mb: 2, p: 1.5, border: '1px dashed', borderRadius: 1.5 }}>
                <Typography variant="subtitle1">{plan.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Monthly: {plan.pricing?.monthly?.final} ({plan.pricing?.monthly?.badge || 'base'})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Annual: {plan.pricing?.annual?.final} ({plan.pricing?.annual?.badge || 'base'})
                </Typography>
              </Box>
            ))}
            {planPreview && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2">Focused plan</Typography>
                <Typography variant="body2" color="text.secondary">
                  {planPreview.description}
                </Typography>
                <Typography variant="body2">Monthly: {planPreview.pricing?.monthly?.final}</Typography>
                <Typography variant="body2">Annual: {planPreview.pricing?.annual?.final}</Typography>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

